import type { DynamicEntityDefinition, SaveEavValuesDto } from '../types/DynamicEntity.types';
import type { DynamicEntitiesFetcher } from './fetcher';
import { resolveFetcher } from './fetcher';
import { DynamicEntitiesHttpError } from './DynamicEntitiesHttpError';

export interface DynamicEntitiesApiOptions {
    baseUrl: string;
    /** Transporte del anfitrión. Ausente → `fetch` global. */
    fetcher?: DynamicEntitiesFetcher;
}

export interface DynamicEntitiesApiClient {
    getDefinition(featureCode: string, entityCode: string): Promise<DynamicEntityDefinition>;
    saveValues(
        featureCode: string,
        entityCode: string,
        recordId: string,
        values: SaveEavValuesDto,
        idEntityMultiple?: string
    ): Promise<void>;
    getValues(
        featureCode: string,
        entityCode: string,
        recordId: string,
        idEntityMultiple?: string
    ): Promise<SaveEavValuesDto>;
    deleteRecord(featureCode: string, recordId: string, idEntityMultiple?: string): Promise<void>;
    getMultipleRecords(
        featureCode: string,
        entityCode: string,
        recordId: string
    ): Promise<SaveEavValuesDto[]>;
}

/**
 * Construye el cliente EAV del módulo con un `baseUrl` y un transporte fijos.
 *
 * Preferir esta factory sobre el objeto `DynamicEntitiesApi`: aquí el transporte
 * viaja con el cliente, así que no hay forma de hacer una petición sin las
 * cabeceras del anfitrión.
 */
export function createDynamicEntitiesApi({ baseUrl, fetcher }: DynamicEntitiesApiOptions): DynamicEntitiesApiClient {
    const http = resolveFetcher(fetcher);

    const valuesUrl = (featureCode: string, entityCode: string, recordId: string, idEntityMultiple?: string) =>
        idEntityMultiple
            ? `${baseUrl}/ea/${featureCode}/${entityCode}/${recordId}/${idEntityMultiple}`
            : `${baseUrl}/ea/${featureCode}/${entityCode}/${recordId}`;

    return {
        async getDefinition(featureCode, entityCode) {
            const url = `${baseUrl}/dynamic-entities/definition?featureCode=${featureCode}&entityCode=${entityCode}`;
            const response = await http(url);
            if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch definition: ${response.status} ${response.statusText}`, response);
            return response.json();
        },

        async saveValues(featureCode, entityCode, recordId, values, idEntityMultiple) {
            const response = await http(valuesUrl(featureCode, entityCode, recordId, idEntityMultiple), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to save values: ${response.status} ${response.statusText}`, response);
        },

        async getValues(featureCode, entityCode, recordId, idEntityMultiple) {
            const response = await http(valuesUrl(featureCode, entityCode, recordId, idEntityMultiple));
            if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch values: ${response.status} ${response.statusText}`, response);
            return response.json();
        },

        async deleteRecord(featureCode, recordId, idEntityMultiple) {
            const url = idEntityMultiple
                ? `${baseUrl}/ea/${featureCode}/${recordId}/${idEntityMultiple}`
                : `${baseUrl}/ea/${featureCode}/${recordId}`;
            const response = await http(url, { method: 'DELETE' });
            if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to delete record: ${response.status} ${response.statusText}`, response);
        },

        async getMultipleRecords(featureCode, entityCode, recordId) {
            const response = await http(`${baseUrl}/ea/${featureCode}/${entityCode}/${recordId}/multiple`);
            if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch records: ${response.status} ${response.statusText}`, response);
            return response.json();
        }
    };
}

/**
 * Fachada histórica que recibe el `baseUrl` en cada llamada.
 *
 * @deprecated Usar {@link createDynamicEntitiesApi} —o el hook
 * `useDynamicEntitiesApi`, que ya lo cablea con el contexto—. Se mantiene para no
 * romper a quien la importe: acepta el mismo `baseUrl` posicional y un `fetcher`
 * opcional al final de cada firma.
 */
export const DynamicEntitiesApi = {
    getDefinition: (
        baseUrl: string,
        featureCode: string,
        entityCode: string,
        fetcher?: DynamicEntitiesFetcher
    ): Promise<DynamicEntityDefinition> =>
        createDynamicEntitiesApi({ baseUrl, fetcher }).getDefinition(featureCode, entityCode),

    saveValues: (
        baseUrl: string,
        featureCode: string,
        entityCode: string,
        recordId: string,
        values: SaveEavValuesDto,
        idEntityMultiple?: string,
        fetcher?: DynamicEntitiesFetcher
    ): Promise<void> =>
        createDynamicEntitiesApi({ baseUrl, fetcher })
            .saveValues(featureCode, entityCode, recordId, values, idEntityMultiple),

    getValues: (
        baseUrl: string,
        featureCode: string,
        entityCode: string,
        recordId: string,
        idEntityMultiple?: string,
        fetcher?: DynamicEntitiesFetcher
    ): Promise<SaveEavValuesDto> =>
        createDynamicEntitiesApi({ baseUrl, fetcher })
            .getValues(featureCode, entityCode, recordId, idEntityMultiple),

    deleteRecord: (
        baseUrl: string,
        featureCode: string,
        recordId: string,
        idEntityMultiple?: string,
        fetcher?: DynamicEntitiesFetcher
    ): Promise<void> =>
        createDynamicEntitiesApi({ baseUrl, fetcher })
            .deleteRecord(featureCode, recordId, idEntityMultiple),

    getMultipleRecords: (
        baseUrl: string,
        featureCode: string,
        entityCode: string,
        recordId: string,
        fetcher?: DynamicEntitiesFetcher
    ): Promise<SaveEavValuesDto[]> =>
        createDynamicEntitiesApi({ baseUrl, fetcher })
            .getMultipleRecords(featureCode, entityCode, recordId)
};
